import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Wrench } from 'lucide-react';

import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { EmptyState } from '../ui/empty-state';
import { Citerne } from '../ui/icons/domain-icons';
import { BarrePagination } from '../ui/pagination-bar';
import { usePagination } from '../../hooks/usePagination';
import {
  REASON_CODE_LABELS,
  SEVERITY_LABELS,
  WORK_STATUS_LABELS,
  WORK_TYPE_LABELS,
} from '../../lib/maintenanceLabels';
import { formatDate, formatDateHeure, formatMontant } from '../../lib/formats';
import { referenceLisible, texteSur } from '../../lib/libelles';

const num = (value) => Number(value || 0);
export const maintenanceMoney = (value) => formatMontant(num(value));
export const maintenanceDateLabel = (value) => formatDate(value);
export const maintenanceDateTime = (value) => formatDateHeure(value);

/**
 * Ce que l'atelier a fait sur ce camion, du plus récent au plus ancien:
 * ordres de travail, pannes signalées, immobilisations.
 */
export function buildMaintenanceTimeline(data) {
  if (!data) return [];
  const events = [
    ...data.orders.map((row) => ({
      id: `wo-${row.id}`,
      at: row.completed_at || row.scheduled_date || row.created_at,
      nature: 'Ordre de travail',
      icon: Wrench,
      title: `${referenceLisible(row.order_number)} · ${row.title}`,
      meta: `${WORK_TYPE_LABELS[row.work_type]} · ${row.workshop_name || 'Atelier interne'} · ${maintenanceMoney(row.total_cost)}`,
      badge: WORK_STATUS_LABELS[row.status],
      variant: row.status === 'done' ? 'success' : 'info',
    })),
    ...data.breakdowns.map((row) => ({
      id: `bd-${row.id}`,
      at: row.reported_at || row.created_at,
      nature: 'Panne',
      icon: AlertTriangle,
      title: texteSur(row.category, 'Organe non précisé'),
      meta: row.description,
      badge: SEVERITY_LABELS[row.severity],
      variant: row.severity === 'critical' ? 'destructive' : 'warning',
    })),
    ...data.immobilizations.map((row) => ({
      id: `im-${row.id}`,
      at: row.started_at || row.created_at,
      nature: 'Immobilisation',
      icon: Citerne,
      title: row.reason,
      meta: `${REASON_CODE_LABELS[row.reason_code]}${
        row.released_at
          ? ` · levée le ${maintenanceDateTime(row.released_at)}`
          : ' · toujours en cours'
      }`,
      badge: row.status === 'active' ? 'En cours' : 'Levée',
      variant: row.status === 'active' ? 'destructive' : 'muted',
    })),
  ];
  return events
    .filter((event) => event.at)
    .sort((a, b) => String(b.at).localeCompare(String(a.at)));
}

export default function MaintenanceTimeline({ data }) {
  const timeline = useMemo(() => buildMaintenanceTimeline(data), [data]);
  const pagination = usePagination(timeline, { cle: 'chronologie' });

  if (timeline.length === 0) {
    return (
      <EmptyState
        variant="empty"
        icon={Wrench}
        title="Aucune intervention enregistrée"
        description="Cette chronologie retrace ce que l’atelier a fait sur ce camion : ordres de travail, pannes signalées, immobilisations. Elle se remplit dès qu’une intervention est ouverte."
        // Rien ne se crée ici: les formulaires vivent sur l'atelier.
        action={
          <Button size="sm" asChild>
            <Link to="/maintenance">Ouvrir l’atelier</Link>
          </Button>
        }
      />
    );
  }

  return (
    <>
      <ol className="space-y-2">
        {pagination.lignes.map((event) => {
          const Icon = event.icon;
          return (
            <li key={event.id} className="flex gap-3 rounded-xl border border-border bg-card p-4">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              </span>
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-semibold text-foreground">{event.title}</p>
                  <Badge variant={event.variant}>{event.badge}</Badge>
                </div>
                <p className="text-sm text-muted-foreground">{event.meta}</p>
                <p className="font-mono text-xs uppercase tracking-[0.08em] text-muted-foreground/70">
                  {event.nature} · {maintenanceDateTime(event.at)}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
      <BarrePagination pagination={pagination} nom="interventions" />
    </>
  );
}
